import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, Edit, Download } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../utils/api';

function formatSize(b) {
  if (!b) return '';
  return b < 1024*1024 ? `${(b/1024).toFixed(0)} KB` : `${(b/1024/1024).toFixed(1)} MB`;
}

// ── List ─────────────────────────────────────────────────
export function AdminPublicationsList() {
  const [pubs,  setPubs]  = useState([]);
  const [total, setTotal] = useState(0);
  const [page,  setPage]  = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/publications?page=${page}&limit=15`)
      .then(r => { setPubs(r.data.data); setTotal(r.data.total); setPages(r.data.pages); })
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Fshi "${title}"?`)) return;
    try {
      await api.delete(`/admin/publications/${id}`);
      toast.success('Publikimi u fshi.');
      load();
    } catch { toast.error('Gabim.'); }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">Publikimet</h1>
          <p className="admin-page-sub">{total} publikime gjithsej</p>
        </div>
        <Link to="/admin/publikimet/i-ri" className="btn btn-primary">
          <Plus size={15}/> Publikim i ri
        </Link>
      </div>

      <div className="card">
        {loading ? <div className="spinner"/> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Titulli</th>
                  <th>Lloji</th>
                  <th>Madhësia</th>
                  <th>Viti</th>
                  <th>Shkarkimet</th>
                  <th>Statusi</th>
                  <th>Veprimet</th>
                </tr>
              </thead>
              <tbody>
                {pubs.map(p => (
                  <tr key={p.id}>
                    <td>
                      <span style={{fontSize:'0.875rem',fontWeight:500,color:'var(--gray-900)',display:'block',maxWidth:280,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {p.title}
                      </span>
                    </td>
                    <td><span style={{fontSize:'0.78rem',color:'var(--gray-500)'}}>{p.type_name || '—'}</span></td>
                    <td><span style={{fontSize:'0.78rem',color:'var(--gray-500)'}}>{formatSize(p.file_size)}</span></td>
                    <td><span style={{fontSize:'0.78rem'}}>{p.year || '—'}</span></td>
                    <td><span style={{fontSize:'0.8rem'}}><Download size={12} style={{marginRight:4,verticalAlign:'middle'}}/>{p.downloads}</span></td>
                    <td>
                      <span className={`badge status-${p.status}`}>
                        {p.status === 'published' ? 'Publikuar' : p.status === 'draft' ? 'Draft' : 'Arkivuar'}
                      </span>
                    </td>
                    <td>
                      <div className="action-btns">
                        <Link to={`/admin/publikimet/${p.id}/ndrysho`} className="btn btn-secondary btn-sm"><Edit size={13}/></Link>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p.id, p.title)}><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {pubs.length === 0 && (
                  <tr><td colSpan={7} style={{textAlign:'center',color:'var(--gray-400)',padding:'2rem'}}>Nuk ka publikime.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="pagination" style={{padding:'1rem'}}>
            {Array.from({length:pages},(_,i)=>i+1).map(p=>(
              <button key={p} className={`page-btn ${p===page?'active':''}`} onClick={()=>setPage(p)}>{p}</button>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// ── Form ─────────────────────────────────────────────────
export function AdminPublicationsForm() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isEdit   = Boolean(id);
  const [types, setTypes] = useState([]);
  const [form, setForm]   = useState({
    title: '', description: '', type_id: '', year: '', status: 'published'
  });
  const [file, setFile]             = useState(null);
  const [existingFile, setExistingFile] = useState({path:'', name:''});
  const [loading, setLoading]           = useState(false);
  const currentYear = new Date().getFullYear();
  const years = Array.from({length:10},(_,i)=>currentYear-i);

  useEffect(() => {
    api.get('/publication-types').then(r => setTypes(r.data));
    if (isEdit) {
      api.get(`/admin/publications?page=1&limit=200`).then(r => {
        const found = r.data.data.find(p => String(p.id) === id);
        if (found) {
          setForm({ title: found.title||'', description: found.description||'', type_id: found.type_id||'', year: found.year||'', status: found.status||'published' });
          setExistingFile({ path: found.file_path||'', name: found.file_name||'' });
        }
      });
    }
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !file) return toast.error('Zgjidhni skedarin.');
    setLoading(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k,v]) => fd.append(k, v));
    if (file) {
      fd.append('file', file);
    } else if (isEdit && existingFile.path) {
      fd.append('existing_file', existingFile.path);
      fd.append('existing_file_name', existingFile.name);
    }
    try {
      if (isEdit) {
        await api.put(`/admin/publications/${id}`, fd);
        toast.success('Publikimi u përditësua.');
      } else {
        await api.post('/admin/publications', fd);
        toast.success('Publikimi u ngarkua.');
      }
      navigate('/admin/publikimet');
    } catch(err) {
      toast.error(err.response?.data?.message || 'Gabim.');
    } finally { setLoading(false); }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link', 'image', 'video'],
      ['blockquote', 'code-block'],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', 'image', 'video', 'blockquote', 'code-block',
  ];

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="page-title">{isEdit ? 'Ndrysho Publikimin' : 'Publikim i Ri'}</h1>
        <Link to="/admin/publikimet" className="btn btn-secondary">← Kthehu</Link>
      </div>

      <form onSubmit={handleSubmit} className="news-form-grid">
        <div className="card" style={{padding:'1.25rem'}}>
          <div className="form-group">
            <label className="form-label">Titulli *</label>
            <input className="form-input" required placeholder="Titulli i dokumentit..."
              value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/>
          </div>
          <div className="form-group">
            <label className="form-label">Përshkrimi</label>
            <ReactQuill
              theme="snow"
              modules={quillModules}
              formats={quillFormats}
              value={form.description}
              onChange={(value) => setForm(p => ({ ...p, description: value }))}
              style={{ minHeight: '180px', marginBottom: '0.75rem' }}
            />
            <p style={{ fontSize:'0.72rem', color:'var(--gray-400)', marginTop:4 }}>
              Mund të shtoni tekst, lidhje, imazhe dhe video në përshkrim.
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">{isEdit ? 'Ngarko skedar të ri (opsional)' : 'Skedari *'}</label>

            {/* Show existing file in edit mode */}
            {isEdit && existingFile.name && !existingFile.path.includes('placeholder') && (
              <div style={{
                display:'flex', alignItems:'center', gap:8,
                padding:'8px 12px', background:'var(--gray-50)',
                border:'1px solid var(--gray-200)', borderRadius:6,
                marginBottom:8, fontSize:'0.82rem', color:'var(--gray-700)'
              }}>
                <span>📄</span>
                <span style={{fontWeight:500}}>{existingFile.name}</span>
                <span style={{color:'var(--gray-400)',fontSize:'0.72rem'}}>(skedari aktual)</span>
              </div>
            )}

            <input type="file" className="form-input"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              onChange={e => {
                const f = e.target.files[0];
                setFile(f || null);
              }}
              required={!isEdit}/>
            {file && (
              <p style={{fontSize:'0.72rem',color:'var(--blue-600)',marginTop:4}}>
                ✓ Zgjedhur: <strong>{file.name}</strong> ({(file.size/1024/1024).toFixed(2)} MB)
              </p>
            )}
            <p style={{fontSize:'0.72rem',color:'var(--gray-400)',marginTop:4}}>
              Formatet: PDF, Word, Excel, Imazh — Max 10MB
            </p>
          </div>
        </div>

        <div>
          <div className="card" style={{padding:'1.25rem',marginBottom:'1rem'}}>
            <h3 className="form-side-title">Publikimi</h3>
            <div className="form-group">
              <label className="form-label">Statusi</label>
              <select className="form-input form-select" value={form.status}
                onChange={e=>setForm(p=>({...p,status:e.target.value}))}>
                <option value="published">Publikuar</option>
                <option value="draft">Draft</option>
                <option value="archived">Arkivuar</option>
              </select>
            </div>
            <button type="submit" className="btn btn-primary" style={{width:'100%',justifyContent:'center'}} disabled={loading}>
              {loading ? 'Duke ngarkuar...' : isEdit ? 'Ruaj' : 'Ngarko dokumentin'}
            </button>
          </div>

          <div className="card" style={{padding:'1.25rem',marginBottom:'1rem'}}>
            <h3 className="form-side-title">Kategoria & Viti</h3>
            <div className="form-group">
              <label className="form-label">Lloji</label>
              <select className="form-input form-select" value={form.type_id}
                onChange={e=>setForm(p=>({...p,type_id:e.target.value}))}>
                <option value="">Zgjidh llojin</option>
                {types.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Viti</label>
              <select className="form-input form-select" value={form.year}
                onChange={e=>setForm(p=>({...p,year:e.target.value}))}>
                <option value="">Zgjidh vitin</option>
                {years.map(y=><option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
}
